import {
  Box,
  Grid,
  Typography,
  LinearProgress,
  Checkbox
} from '@mui/material';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps,
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { styled, Theme } from '@mui/material/styles';
import { SxProps } from '@mui/system';

import React from 'react';
import sortBy from 'sort-by';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Column } from '@material-table/core';
import { ColumnDef, Row } from '@tanstack/react-table';
import { IAssociatesReport, IAssociatesReportPage } from '../../Interface';
import { TanstackTable } from 'Shared/Components/Mui/TanstackTable/index';
import RegisterStructure from 'Shared/Constant/AssociatesReport/RecordStructure';
import { IFieldStructure } from 'Shared/Interfaces/ObjectSalesforce';
import { KEY_ASSOCIATESREPORT_SELECTED } from 'Shared/Constant/Cache';
import ModalAssociateDetail from '../AssociateReportModalSupplier/index';
import ModalAssociateProductDetail from '../AssociateReportModalProduct/index';
import ExportFilePage from './_exportfile';
import convertToNumber from 'Shared/Utils/ConvertToNumber';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)',
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

function AssociatesReportFiltersPage(props: IAssociatesReportPage) {
  const parsePtBrNumber = React.useCallback((value: any): number => {
    if (value == null) {
      return 0;
    }
    if (typeof value === 'number') {
      return Number.isFinite(value) ? value : 0;
    }
    const normalized = String(value).trim().replace(/\./g, '').replace(/,/g, '.');
    const parsed = parseFloat(normalized);
    return Number.isNaN(parsed) ? 0 : parsed;
  }, []);

  const [showResume, setShowResume] = React.useState<boolean>(true);
  // const [showResumeProvider, setShowResumeProvider] = React.useState<boolean>(false);
  const [showButtonResume, setShowButtonResume] = React.useState<boolean>(false);

  const [columns, setColumns] = React.useState<Column<object>[]>([]);
  const toggleSetColumns = (values: Column<object>[]) => {
    setColumns(values);
  }

  const [selectedRowGuid, setSelectedRowGuid] = React.useState<string>('');
  const tanstackColumns = React.useMemo<ColumnDef<object, any>[]>(() => {
    return columns
      .filter((column) => Boolean(column.field) && !(column.hidden ?? false))
      .map<ColumnDef<object, any>>((column) => {
        const field = column.field ? column.field.toString() : '';
        const align = column.align === 'right' ? 'right' : 'left';
        const headerStyle = column.headerStyle as React.CSSProperties | undefined;
        const cellStyle = column.cellStyle as React.CSSProperties | undefined;

        const baseColumn: ColumnDef<object, any> = {
          id: field,
          accessorKey: field,
          header: String(column.title || field),
          cell: column.render
            ? ({ row }) => column.render?.(row.original)
            : ({ getValue }) => {
              const value = getValue();
              return value == null ? '' : value;
            },
          meta: {
            align,
            headerSx: headerStyle as SxProps<Theme> | undefined,
            cellSx: cellStyle as SxProps<Theme> | undefined,
          },
        };
        if (column.customSort) {
          baseColumn.sortingFn = (rowA, rowB, columnId) => {
            const firstOriginal = rowA.original as Record<string, unknown>;
            const secondOriginal = rowB.original as Record<string, unknown>;
            try {
              return column.customSort!(firstOriginal as any, secondOriginal as any, columnId as any);
            } catch (_) {
              const firstValue = parsePtBrNumber(rowA.getValue(columnId));
              const secondValue = parsePtBrNumber(rowB.getValue(columnId));
              return firstValue - secondValue;
            }
          };
        }
        return baseColumn;
      });
  }, [columns, parsePtBrNumber]);

  const [data, setData] = React.useState<object[]>([]);
  const toggleSetData = (values: [IAssociatesReport?]) => {
    const _array: object[] = values.map((reg, index) => {
      if (!reg) {
        return {};
      }

      const metaMixValue = showResume ? convertToNumber(reg.metaMixPrincipal || '0') : convertToNumber(reg.metaMix || '0');
      const mixValueToUse = showResume ? convertToNumber(reg.mixPrincipal || '0') : convertToNumber(reg.mix || '0');
      const percentMixValue = metaMixValue > 0 ? (mixValueToUse / metaMixValue) * 100 : 0;

      return {
        ...reg,
        guidcontrol: reg.id,
        id: `${index + 1}`,
        percentMix: Number(percentMixValue).toLocaleString("pt-BR", {
          minimumIntegerDigits: 2,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })
      };
    });

    setData(_array);
  }

  const [expanded, setExpanded] = React.useState(true);
  const toggleExpanded = (value: boolean) => {
    setExpanded(value);
  };

  const toggleSetItemSelected = (item: any) => {
    const _itemAssociatesReport: IAssociatesReport = item;

    if (props.toggleSetAssociatesReportSelected) {
      props.toggleSetAssociatesReportSelected(_itemAssociatesReport);
    }
    if (props.toggleSetCache) {
      props.toggleSetCache(KEY_ASSOCIATESREPORT_SELECTED, _itemAssociatesReport);
    }

    if (props.toggleShowModalAssociateDetail) {
      props.toggleShowModalAssociateDetail(true);
    }
  }

  const handleRowClick = (row: Row<object>) => {
    const rowData = row.original as IAssociatesReport;
    const guidValue = rowData.guidcontrol || `${row.index}`;
    setSelectedRowGuid(guidValue);
    toggleSetItemSelected(rowData);
  };

  const getFieldsStructure = (list: IFieldStructure[]) => {
    let _array: Column<object>[] = [];
    if (list && list.length > 0) {
      for (let regField of list.sort(sortBy('orderFormMain'))) {
        let _column: Column<object> = {}
        if (regField.viewFormMain) {
          const hiddenDefault = regField.hiddenFormMain || false;
          const hiddenMix = showResume && regField.nameField === 'mix';
          const hiddenMixPrincipal = !showResume && regField.nameField === 'mixPrincipal';
          const hiddenMetaMix = showResume && regField.nameField === 'metaMix';
          const hiddenMetaMixPrincipal = !showResume && regField.nameField === 'metaMixPrincipal';
          const hiddenField = hiddenDefault || hiddenMix || hiddenMixPrincipal || hiddenMetaMix || hiddenMetaMixPrincipal;
          switch (regField.type) {
            case 'checkbox':
              _column = {
                title: regField.label,
                field: regField.nameField,
                editable: regField.edit ? 'always' : 'never',
                hidden: hiddenField,
                editComponent: (props) => {
                  return (
                    <input
                      type="checkbox"
                      checked={props.value}
                      onChange={(e) => props.onChange(e.target.checked)}
                    />
                  );
                },
                render: (rowdata: any) => {
                  if (regField.nameField === 'active') {
                    return (<input type="checkbox" checked={rowdata.active} readOnly />)
                  }
                  else if (regField.nameField === 'isMeta') {
                    return (<input type="checkbox" checked={rowdata.isMeta} readOnly />)
                  }
                }
              }
              break;
            case 'currency':
              const fieldName = regField.nameField;
              _column = {
                title: regField.label,
                field: regField.nameField,
                customSort: (a: any, b: any) => {
                  if (!fieldName) {
                    return 0;
                  }
                  return parsePtBrNumber(a?.[fieldName]) - parsePtBrNumber(b?.[fieldName]);
                },
                type: 'numeric', // numeric
                align: 'right',
                editable: regField.edit ? 'always' : 'never',
                hidden: hiddenField
              }
              break;
            default:
              _column = {
                title: regField.label,
                field: regField.nameField,
                type: 'string',
                editable: regField.edit ? 'always' : 'never',
                hidden: hiddenField
              }
              break;
          }
          switch (regField.label?.toUpperCase()) {
            case 'ID':
              _column.searchable = false;
              break;
            default:
              _column.searchable = true;
              break;
          }

          if (regField.defaultGroupOrder) {
            _column.defaultGroupOrder = regField.defaultGroupOrder;
          }

          if (regField.defaultSort) {
            _column.defaultSort = regField.defaultSort === 'asc' ? 'asc' : 'desc';
          }
          if (regField.width) {
            _column.width = regField.width;
          }
          if (regField.cellStyle) {
            _column.cellStyle = regField.cellStyle;
          }


          switch (_column.field) {
            case 'qtCxFaturada':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.qtCxFaturada) - parsePtBrNumber(b?.qtCxFaturada);
              _column.type = 'numeric';
              break;
            case 'vlrVenda':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.vlrVenda) - parsePtBrNumber(b?.vlrVenda);
              _column.type = 'numeric';
              break;
            case 'valorBase':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.valorBase) - parsePtBrNumber(b?.valorBase);
              _column.type = 'numeric';
              // _column.render = (rowData: any) => parseFloat(rowData.valorBase.replace(/\./g, '').replace(/,/g, '.')).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', currencyDisplay: 'narrowSymbol' }).replace('R$', ''); // Formata o número no formato pt-br sem moeda
              break;
            case 'qtdeBase':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.qtdeBase) - parsePtBrNumber(b?.qtdeBase);
              _column.type = 'numeric';
              break;
            case 'metaMix':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.metaMix) - parsePtBrNumber(b?.metaMix);
              _column.type = 'numeric';
              break;
            case 'mix':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.mix) - parsePtBrNumber(b?.mix);
              _column.type = 'numeric';
              break;
            case 'mixPrincipal':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.mixPrincipal) - parsePtBrNumber(b?.mixPrincipal);
              _column.type = 'numeric';
              break;
            case 'percentMix':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.percentMix) - parsePtBrNumber(b?.percentMix);
              _column.type = 'numeric';
              break;
            case 'metaFaturamento':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.metaFaturamento) - parsePtBrNumber(b?.metaFaturamento);
              _column.type = 'numeric';
              break;
            case 'faturamento':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.faturamento) - parsePtBrNumber(b?.faturamento);
              _column.type = 'numeric';
              break;
            case 'percentFaturmento':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.percentFaturmento) - parsePtBrNumber(b?.percentFaturmento);
              _column.type = 'numeric';
              break;
            case 'caixas':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.caixas) - parsePtBrNumber(b?.caixas);
              _column.type = 'numeric';
              break;
            case 'qtUnitCx':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.qtUnitCx) - parsePtBrNumber(b?.qtUnitCx);
              _column.type = 'numeric';
              break;
            case 'vlrUnitario':
              _column.customSort = (a: any, b: any) => parsePtBrNumber(a?.vlrUnitario) - parsePtBrNumber(b?.vlrUnitario);
              _column.type = 'numeric';
              break;
            default:
              break;
          }

          _array.push(_column)
        }
      }
    }
    toggleSetColumns(_array);

  };

  React.useEffect(() => {
    if (showResume) {
      toggleSetData(props.listAssociatesReportResume);
    }
    else {
      toggleSetData(props.listAssociatesReport);
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResume, props.listAssociatesReport, props.listAssociatesReportResume, props.dateUpdateInfo])

  React.useEffect(() => {
    getFieldsStructure(RegisterStructure);
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResume]);

  React.useEffect(() => {
    if (props.showButtonResume) {
      setShowButtonResume(true);
    }
    else {
      setShowButtonResume(false);
    }
  }, [props.showButtonResume])

  React.useEffect(() => {
    if (props.showResume) {
      setShowResume(true);
    }
    else {
      setShowResume(false);
    }
  }, [props.showResume])

  // React.useEffect(() => {
  //   if (props.showResumeProvider) {
  //     setShowResumeProvider(true);
  //   }
  //   else {
  //     setShowResumeProvider(false);
  //   }
  // }, [props.showResumeProvider])

  // toggleShowResume
  const handleExportFile = {
    ...props,
    showResume
  }

  return (
    <Box id="AssociatesReportFilter" key="AssociatesReportFilter">
      <Grid container>
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
          <Accordion
            expanded={expanded}
            onChange={() => { toggleExpanded(!expanded); }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <Typography component="div" align="left">
                <span className="FormTitle">Resultado</span>
                {props.showLoadingUpdateTable && (
                  <Box component="span" sx={{ display: 'inline-flex', width: 120 }}>
                    <LinearProgress />
                  </Box>
                )}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Typography id="AssociatesReportResultTableExport" component="div" align="right">
                <ExportFilePage {...handleExportFile} />
              </Typography>
              <React.Fragment>
                {showButtonResume ?
                  <Typography component="div" align="right">
                    <Checkbox
                      checked={showResume}
                      onChange={() => {
                        if (props.toggleShowResume) {
                          props.toggleShowResume(!showResume)
                        }
                      }}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                    <span>Agrupar Matriz/Filial</span>
                  </Typography>
                  :
                  <Typography component="div" align="right">
                    <span>&nbsp;</span>
                  </Typography>
                }
              </React.Fragment>
              <Typography id="AssociatesReportResultTable" component="div" align="left">
                {props.showLoadingThisPage ? (
                  <LinearProgress />
                ) : (
                  <TanstackTable
                    columns={tanstackColumns}
                    data={data}
                    title="Associados"
                    isLoading={props.showLoadingThisPage}
                    enableGlobalFilter
                    stickyHeader
                    onRowClick={handleRowClick}
                    rowSx={(row) => ({
                      cursor: 'pointer',
                      backgroundColor:
                        selectedRowGuid === (row.original as IAssociatesReport).guidcontrol
                          ? '#F5F5F5'
                          : undefined,
                    })}
                    rowsPerPageOptions={[5, 10, 20, 50]}
                    paperSx={{
                      borderRadius: 2,
                      overflow: 'hidden',
                      boxShadow: '0 10px 40px rgba(15,23,42,0.15)',
                    }}
                    containerSx={{
                      '& td': { minHeight: 48 },
                    }}
                  />
                )}
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Grid>
      </Grid>
      {props.showModalAssociateDetail &&
        <ModalAssociateDetail {...props} />
      }
      {props.showModalAssociateProductDetail &&
        <ModalAssociateProductDetail {...props} />
      }
    </Box>
  );
}

export default AssociatesReportFiltersPage;
